import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import LockResetIcon from "@mui/icons-material/LockReset";
import LoginIcon from "@mui/icons-material/Login";

import HiddenTextField from "../components/HiddenTextField";
import { useResetUserPassword } from "../api/bpm/endpoints";
import LanguageSwitcher from "../components/LanguageSwitcher";

export const Route = createFileRoute("/forgot-password/$passwordResetCode")({
  component: ResetPasswordComponent,
  staticData: {
    title: "routes.resetPassword",
  },
});

function ResetPasswordComponent() {
  const { t } = useTranslation();
  const { passwordResetCode } = Route.useParams();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const resetMutation = useResetUserPassword();

  const passwordMinLength = 6;
  const passwordTooShort = newPassword.length > 0 && newPassword.length < passwordMinLength;
  const passwordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const isInvalid =
    !passwordResetCode ||
    newPassword.length < passwordMinLength ||
    confirmPassword.length < passwordMinLength ||
    newPassword !== confirmPassword;

  const handleSubmit = () => {
    if (isInvalid) return;

    setError("");
    resetMutation.mutate(
      {
        data: {
          senha: newPassword,
          confirmacaoSenha: confirmPassword,
        },
        params: {
          passwordResetCode,
        },
      },
      {
        onSuccess: () => {
          setSuccess(true);
        },
        onError: () => {
          setError(t("resetPassword.errorMessage"));
        },
      }
    );
  };

  if (!passwordResetCode) {
    return (
      <ResetPasswordLayout>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("resetPassword.invalidCode")}
        </Alert>
        <Button
          component={Link}
          to="/login"
          fullWidth
          variant="contained"
          startIcon={<LoginIcon />}
        >
          {t("resetPassword.goToLogin")}
        </Button>
      </ResetPasswordLayout>
    );
  }

  if (success) {
    return (
      <ResetPasswordLayout>
        <Alert severity="success" sx={{ mb: 2 }}>
          {t("resetPassword.successMessage")}
        </Alert>
        <Button
          component={Link}
          to="/login"
          fullWidth
          variant="contained"
          startIcon={<LoginIcon />}
        >
          {t("resetPassword.goToLogin")}
        </Button>
      </ResetPasswordLayout>
    );
  }

  return (
    <ResetPasswordLayout>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <HiddenTextField
          variant="outlined"
          fullWidth
          label={t("resetPassword.newPassword")}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={passwordTooShort}
          helperText={passwordTooShort ? t("resetPassword.passwordMinLength") : " "}
          autoComplete="new-password"
          autoFocus
        />

        <HiddenTextField
          variant="outlined"
          fullWidth
          label={t("resetPassword.confirmPassword")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={passwordMismatch}
          helperText={passwordMismatch ? t("resetPassword.passwordMismatch") : " "}
          autoComplete="new-password"
        />

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        <Button
          fullWidth
          variant="contained"
          color="primary"
          disabled={isInvalid || resetMutation.isPending}
          onClick={handleSubmit}
          startIcon={
            resetMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <LockResetIcon />
            )
          }
          sx={{ mt: 1 }}
        >
          {t("resetPassword.resetButton")}
        </Button>

        <Button
          component={Link}
          to="/login"
          fullWidth
          variant="text"
          color="primary"
        >
          {t("resetPassword.goToLogin")}
        </Button>
      </Box>
    </ResetPasswordLayout>
  );
}

function ResetPasswordLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        py: 4,
      }}
    >
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <LanguageSwitcher />
      </Box>

      <Stack
        component="section"
        justifyContent="center"
        alignItems="center"
        spacing={3}
        sx={{ width: "100%", maxWidth: 480, px: 2 }}
      >
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <img width="180px" src="/assets/logo.svg" alt="logo sosdocs" />
        </Box>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: `0 8px 40px ${alpha(theme.palette.primary.main, 0.08)}`,
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <LockResetIcon sx={{ fontSize: 28, color: "primary.main" }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
              {t("resetPassword.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("resetPassword.subtitle")}
            </Typography>
          </Box>

          {children}
        </Paper>

        {window.versionSystem?.trim() && (
          <Typography variant="caption" color="text.secondary">
            {t("common.version")} {window.versionSystem}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
